import React, { useState } from 'react';
import StepHeader from './StepHeader';
import { Field, Textarea, Input } from '../../lib/ui/Field';
import { ErrorBanner, Badge } from '../../lib/ui/Feedback';
import { Row } from '../../lib/ui/Card';
import Button from '../../lib/ui/Button';

function PayTile({ active, accent, icon, label, onClick }) {
  const activeCls = accent === 'blue'
    ? 'border-sky-600 bg-sky-50 text-sky-700'
    : 'border-brand bg-brand-light text-brand-dark';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 min-w-[86px] py-3.5 px-2 rounded-2xl border-2 font-extrabold text-center transition-colors ${active ? activeCls : 'border-black/10 bg-white text-ink-soft'}`}
    >
      <div className="text-lg">{icon}</div>
      <div className="text-xs mt-0.5">{label}</div>
    </button>
  );
}

export default function CheckoutStep({
  notes, setNotes,
  loading, error, upiSettings,
  deliveryType, setDeliveryType, immediateCharge,
  walletBalance, payMethod, setPayMethod,
  promoCode, setPromoCode, promoResult, promoLoading, promoError,
  availablePromos = [],
  cart, total, te, L, t,
  pricingPreview,
  selectedAddressLabel,
  handleSubmit, handleApplyPromo, removePromo, onSelectPromo,
  onClose, onBack, onSupportOpen,
}) {
  const promoDiscount = pricingPreview?.promo_discount != null ? Number(pricingPreview.promo_discount) : (promoResult ? (promoResult.discount || promoResult.promo_discount || 0) : 0);
  const deliveryCharge = pricingPreview?.delivery_charge != null ? Number(pricingPreview.delivery_charge) : (deliveryType === 'immediate' ? immediateCharge : 0);
  const itemsTotal = pricingPreview?.items_total != null ? Number(pricingPreview.items_total) : total;
  const walletAmt = payMethod === 'wallet'
    ? (pricingPreview?.wallet_amount != null ? Number(pricingPreview.wallet_amount) : Math.min(walletBalance, Math.max(0, itemsTotal + deliveryCharge - promoDiscount)))
    : (pricingPreview?.wallet_amount || 0);
  const displayTotal = pricingPreview?.total_amount != null ? Number(pricingPreview.total_amount) : Math.max(0, itemsTotal + deliveryCharge - promoDiscount);
  const grandTotal = pricingPreview?.amount_due != null ? Number(pricingPreview.amount_due) : Math.max(0, displayTotal - (payMethod === 'wallet' ? walletAmt : 0));
  const amountShown = payMethod === 'wallet' ? grandTotal : (pricingPreview?.amount_due != null ? Number(pricingPreview.amount_due) : displayTotal);
  const freeDeliveryMin = Number(pricingPreview?.delivery_charge_min_order || 0);
  const [offersOpen, setOffersOpen] = useState(false);
  const eligiblePromos = availablePromos.filter((p) => p.eligible);

  return (
    <div>
      <StepHeader title={`💳 ${L('Payment', 'చెల్లింపు')}`} onClose={onClose} />

      {selectedAddressLabel ? (
        <div className="bg-brand-light/40 rounded-xl px-3 py-2.5 mb-3.5 text-[13px]">
          <div className="text-[11px] font-extrabold text-brand uppercase mb-0.5">{L('Delivering to', 'డెలివరీ')}</div>
          <div className="font-bold text-brand-dark">📍 {selectedAddressLabel}</div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1.5 mb-3.5">
        <Badge>🔒 {L('Secure checkout', 'సురక్షిత చెల్లింపు')}</Badge>
        <Badge>✅ {L('Fresh quality promise', 'తాజా నాణ్యత హామీ')}</Badge>
        <Badge>🚚 {L('Group delivery (scheduled)', 'గ్రూప్ డెలివరీ (షెడ్యూల్)')}</Badge>
      </div>

      <div className="bg-black/[.02] border border-black/5 rounded-2xl px-3.5 py-3 mb-3.5">
        <div className="text-[11px] font-extrabold text-ink-faint uppercase tracking-wide mb-2">{L('Bill details', 'బిల్ వివరాలు')}</div>
        <Row label={`${L('Items', 'వస్తువులు')} (${cart.length})`} value={`₹${itemsTotal.toFixed(2)}`} />
        {promoDiscount > 0 ? <Row label={L('Discount', 'తగ్గింపు')} value={`−₹${Number(promoDiscount).toFixed(2)}`} color="#2D6A27" /> : null}
        <Row label={L('Delivery', 'డెలివరీ')} value={deliveryCharge > 0 ? `₹${deliveryCharge.toFixed(2)}` : L('FREE', 'ఉచితం')} color={deliveryCharge > 0 ? '#B8873B' : '#2D6A27'} />
        {deliveryType === 'normal' && freeDeliveryMin > 0 && deliveryCharge > 0 ? (
          <div className="text-[11px] text-ink-faint font-bold mt-1">
            {L('Free delivery from', 'ఉచిత డెలివరీ')} ₹{freeDeliveryMin.toFixed(2)}
          </div>
        ) : null}
        {payMethod === 'wallet' && walletAmt > 0 ? <Row label={L('Wallet', 'వాలెట్')} value={`−₹${Number(walletAmt).toFixed(2)}`} color="#2D6A27" /> : null}
        <div className="flex justify-between mt-2 pt-2 border-t border-black/10 font-black text-[15px] text-ink">
          <span>{payMethod === 'wallet' ? L('Amount due', 'చెల్లించాల్సినది') : t.total}</span>
          <span>₹{amountShown.toFixed(2)}</span>
        </div>
      </div>

      <Field label={L('Address / Notes', 'చిరునామా / గమనికలు')}>
        <Textarea className="h-14" placeholder={L('Door no, street, landmark…', 'ఇంటి నంబర్, వీధి, మైలురాయి…')} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>

      <Field label={L('Promo Code', 'ప్రోమో కోడ్')}>
        <div className="flex gap-2">
          <Input
            className="flex-1 uppercase tracking-wide"
            placeholder={L('Enter code e.g. NRK100', 'కోడ్ నమోదు చేయండి')}
            value={promoCode}
            disabled={!!promoResult}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
          />
          <Button
            variant={promoResult ? 'danger' : 'accent'}
            loading={promoLoading}
            onClick={promoResult ? removePromo : handleApplyPromo}
          >
            {promoResult ? L('✕ Remove', '✕ తొలగించు') : L('Apply', 'వర్తించు')}
          </Button>
        </div>
        {promoResult ? (
          <div className="mt-1.5 px-3 py-2 bg-emerald-50 rounded-lg text-[13px] text-emerald-800 font-extrabold">
            🎉 {L('Promo applied! You save', 'ప్రోమో వర్తించింది!')} ₹{promoResult.discount || promoResult.promo_discount}
          </div>
        ) : null}
        {promoError ? <div className="mt-1.5 text-xs text-red-600 font-bold">⚠️ {promoError}</div> : null}
        {!promoResult && eligiblePromos.length > 0 ? (
          <div className="mt-2.5">
            <button
              type="button"
              onClick={() => setOffersOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-brand-light/50 border border-brand-light"
            >
              <span className="text-[13px] font-extrabold text-brand-dark">🎁 {L('Available offers', 'అందుబాటులో ఉన్న ఆఫర్లు')} ({eligiblePromos.length})</span>
              <span className={`text-brand-dark transition-transform ${offersOpen ? 'rotate-180' : ''}`}>⌄</span>
            </button>
            {offersOpen ? (
              <div className="flex flex-col gap-1.5 mt-1.5">
                {eligiblePromos.map((p) => {
                  const active = (promoCode || '').toUpperCase() === p.code;
                  return (
                    <button
                      key={p.code}
                      type="button"
                      disabled={promoLoading}
                      onClick={() => onSelectPromo && onSelectPromo(p.code)}
                      className={`text-left px-3 py-2.5 rounded-xl border-[1.5px] ${active ? 'bg-brand-light border-brand' : 'bg-white border-brand-light'}`}
                    >
                      <div className="flex justify-between gap-2">
                        <span className="font-black text-brand-dark tracking-wide">{p.code}</span>
                        <span className="text-[11px] font-extrabold text-brand">
                          {p.discount > 0 ? `−₹${p.discount}` : L('Tap to apply', 'వర్తించండి')}
                        </span>
                      </div>
                      <div className="text-xs text-ink-soft mt-0.5">{p.description || ''}{p.min_order > 0 ? ` · ${L('Min', 'కనీసం')} ₹${p.min_order}` : ''}</div>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}
      </Field>

      <div className="mb-3.5">
        <div className="text-xs font-bold text-ink-soft uppercase tracking-wide mb-2">{L('Delivery Type', 'డెలివరీ రకం')}</div>
        <div className="flex gap-2.5">
          <button type="button" onClick={() => setDeliveryType('normal')} className={`flex-1 py-3.5 px-2 rounded-2xl border-2 text-center font-extrabold ${deliveryType === 'normal' ? 'border-brand bg-brand-light text-brand-dark' : 'border-black/10 bg-white text-ink-soft'}`}>
            <div>🕒</div>
            <div className="text-xs mt-0.5">{L('Standard', 'సాధారణ')}</div>
            <div className="text-[11px] font-semibold text-brand mt-0.5">{deliveryType === 'normal' && deliveryCharge > 0 ? `+₹${deliveryCharge.toFixed(2)}` : L('Group delivery', 'గ్రూప్ డెలివరీ')}</div>
          </button>
          <button type="button" onClick={() => setDeliveryType('immediate')} className={`flex-1 py-3.5 px-2 rounded-2xl border-2 text-center font-extrabold ${deliveryType === 'immediate' ? 'border-gold bg-gold-light text-gold' : 'border-black/10 bg-white text-ink-soft'}`}>
            <div>⚡</div>
            <div className="text-xs mt-0.5">{L('Priority', 'ప్రాధాన్యం')}</div>
            <div className="text-[11px] font-semibold mt-0.5">{deliveryType === 'immediate' && deliveryCharge > 0 ? `+₹${deliveryCharge.toFixed(2)}` : (immediateCharge > 0 ? `+₹${immediateCharge}` : L('Same charges', 'అదే చార్జ్'))}</div>
          </button>
        </div>
      </div>

      <div className="mb-3.5">
        <div className="text-xs font-bold text-ink-soft uppercase tracking-wide mb-2">{L('Payment Method', 'చెల్లింపు పద్ధతి')}</div>
        <div className="flex gap-2.5 flex-wrap">
          <PayTile active={payMethod === 'cod'} icon="💵" label={L('Cash on delivery', 'క్యాష్ ఆన్ డెలివరీ')} onClick={() => setPayMethod('cod')} />
          {upiSettings.razorpay_enabled ? (
            <PayTile active={payMethod === 'razorpay'} accent="blue" icon="💳" label={L('Pay Online', 'ఆన్‌లైన్ చెల్లింపు')} onClick={() => setPayMethod('razorpay')} />
          ) : null}
          {upiSettings.cashfree_enabled && !upiSettings.razorpay_enabled ? (
            <PayTile active={payMethod === 'cashfree'} accent="blue" icon="💳" label={L('Pay Online', 'ఆన్‌లైన్ చెల్లింపు')} onClick={() => setPayMethod('cashfree')} />
          ) : null}
          {!upiSettings.cashfree_enabled && !upiSettings.razorpay_enabled && upiSettings.upi_enabled ? (
            <PayTile active={payMethod === 'upi'} accent="blue" icon="📱" label={L('UPI', 'UPI')} onClick={() => setPayMethod('upi')} />
          ) : null}
        </div>
        <div className="mt-2 text-xs text-ink-faint font-bold">
          {payMethod === 'razorpay'
            ? L('PhonePe, GPay, UPI ID, cards & netbanking — next screen.', 'తదుపరి స్క్రీన్‌లో PhonePe, GPay, UPI ID, కార్డులు & నెట్‌బ్యాంకింగ్.')
            : payMethod === 'cashfree'
              ? L('UPI, cards & netbanking via Cashfree (secure).', 'Cashfree ద్వారా UPI, కార్డులు & నెట్‌బ్యాంకింగ్ (సురక్షితం).')
              : payMethod === 'upi'
                ? L('UPI apps supported. You will confirm payment in next step.', 'UPI యాప్స్ సపోర్ట్ ఉన్నాయి. తదుపరి స్టెప్‌లో చెల్లింపును నిర్ధారించండి.')
                : L('Pay cash safely at delivery.', 'డెలివరీ సమయంలో సురక్షితంగా నగదు చెల్లించండి.')}
        </div>
      </div>

      <ErrorBanner>{error}</ErrorBanner>

      <div className="flex gap-2.5">
        <Button variant="danger" onClick={onBack}>← {L('Back', 'వెనక్కి')}</Button>
        <Button
          variant={payMethod === 'cod' ? 'primary' : 'blue'}
          className="flex-[2]"
          loading={loading}
          onClick={handleSubmit}
        >
          {payMethod === 'razorpay' || payMethod === 'cashfree'
            ? `💳 ${L('Place Order & Pay', 'ఆర్డర్ చేసి చెల్లించు')} ₹${amountShown.toFixed(2)}`
            : payMethod === 'upi'
              ? `📱 ${L('Place Order & Pay', 'ఆర్డర్ చేసి చెల్లించు')} ₹${amountShown.toFixed(2)}`
              : `✅ ${L('Place Order', 'ఆర్డర్ చేయి')} ₹${amountShown.toFixed(2)}`}
        </Button>
      </div>

      <div className="mt-2.5 text-[11px] text-ink-faint text-center font-bold">
        {L('By placing order, you agree to delivery and refund terms.', 'ఆర్డర్ చేస్తే డెలివరీ మరియు రీఫండ్ నిబంధనలను అంగీకరించినట్లే.')}
      </div>

      <div className="flex items-center justify-center gap-3.5 mt-3.5 pt-3 border-t border-black/5 flex-wrap">
        <span className="text-ink-faint text-xs">{L('Need help?', 'సహాయం కావాలా?')}</span>
        <button type="button" onClick={onSupportOpen} className="bg-gold-light text-gold rounded-full px-4 py-1.5 font-extrabold text-xs">🆘 {L('Contact Support', 'సపోర్ట్ సంప్రదించు')}</button>
      </div>
    </div>
  );
}
